import { Breakpoint, capitalize } from '@mui/material';
import { StyleRules } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { breakpoints } from 'const';
import { CSSProperties, MutableRefObject, useMemo } from 'react';

import { useBreakpoint } from './useBreakpoint';

type Override = [string, any];

const getResponsiveClasses = (overrides: Override): StyleRules => {
  const baseKey = overrides[0];
  const resp = Object.keys(overrides[1])
    .filter((key) => breakpoints.keys.includes(key as Breakpoint))
    .reduce((acc, key) => {
      acc[`${baseKey}${capitalize(key)}`] = { ...overrides[1]['base'], ...overrides[1][key] };
      return acc;
    }, {} as Record<string, CSSProperties>);
  return {
    [baseKey]: overrides[1]['base'],
    ...resp,
  };
};

export const useMakeResponsiveStyles = <TStyles extends StyleRules>(
  containerRef: MutableRefObject<HTMLElement | null>,
  styles: TStyles
): Record<keyof typeof styles, string> => {
  const breakpoint = useBreakpoint(containerRef);

  const responsive = useMemo(
    () =>
      Object.entries(styles).reduce((acc, style) => {
        return {
          ...acc,
          ...getResponsiveClasses(style),
        };
      }, {} as StyleRules),
    [styles]
  );

  const responsiveStyles = makeStyles(() => {
    return createStyles(responsive);
  })();

  const responsiveClasses = useMemo(() => {
    const newClasses: Record<string, string> = {};
    Object.keys(styles).forEach((key) => {
      const availableBreakPoints = Object.keys(styles[key])
        .filter((key) => key !== 'base')
        .map((key) => capitalize(key));
      const currentStyle = availableBreakPoints.includes(breakpoint)
        ? `${key}${breakpoint}`
        : `${key}`;
      newClasses[key] = responsiveStyles[currentStyle];
    });
    return newClasses;
  }, [breakpoint, responsiveStyles, styles]);

  return responsiveClasses as Record<keyof typeof styles, string>;
};
